import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { Check, Plus, ArrowRight } from "lucide-react";

const PackageSection = () => {
  const { language } = useLanguage();
  
  const content = {
    sl: {
      label: "CELOTEN PAKET",
      title: "Vse, kar dobite, ko",
      titleHighlight: "prestopite k nam.",
      description: "Ne le knjigovodjo. Celoten finančni oddelek — za delček cene lastnega zaposlenega.",
      cta: "Želim fiksno ponudbo",
      included: [
        {
          title: "Vaš osebni skrbnik",
          text: "Ena oseba, ki pozna vaše podjetje — in se oglasi na telefon.",
          badge: "VKLJUČENO"
        },
        {
          title: "Brezpapirno knjigovodstvo in razumljiva mesečna poročila",
          text: "Vsak mesec veste, kje ste — ne le enkrat na leto.",
          badge: "VKLJUČENO"
        },
        {
          title: "Obračuni DDV",
          text: "Pravočasno in pravilno, vsak mesec ali četrtletje.",
          badge: "VKLJUČENO"
        },
        {
          title: "Obračun plač",
          text: "Zaposleni pravilno in zakonito plačani — tudi tuji delavci.",
          badge: "VKLJUČENO"
        },
        {
          title: "BONUS: Prestop uredimo namesto vas",
          text: "Uredimo predajo od prejšnjega računovodje — tudi med letom.",
          badge: "BREZPLAČNO",
          isBonus: true
        }
      ],
      additionalHeader: "DODATNE STORITVE — OBRAČUNAMO POSEBEJ, PO VNAPREJ DOGOVORJENI CENI",
      additional: [
        { title: "Vračila DDV v EU", text: "Zahtevki za vračilo DDV za gorivo, cestnine in storitve, plačane v tujini." },
        { title: "Kadrovska dokumentacija", text: "Pogodbe o zaposlitvi, napotnice za zdravniške preglede, prijave v zavarovanje, obrazci A1." },
        { title: "Vloge za delovna dovoljenja", text: "Enotna dovoljenja za prebivanje in delo, podaljšanja." },
        { title: "Poročila za banke", text: "Priprava dokumentacije in poročil za pridobitev financiranja." },
        { title: "Ostale storitve", text: "Davčno svetovanje, ustanovitve, sanacije in drugo — po dogovoru." }
      ],
      transparent: {
        label: "TRANSPARENTNA CENA",
        title: "Osnovne storitve v eni fiksni mesečni ceni.",
        text: "Dodatne storitve vedno vnaprej dogovorjene — brez presenečenj na računu. Pisna ponudba v 48 urah."
      }
    },
    en: {
      label: "THE COMPLETE PACKAGE",
      title: "Here's everything you get when you",
      titleHighlight: "move to us.",
      description: "Not a bookkeeper. A finance department — for a fraction of what hiring one would cost.",
      cta: "Get my fixed quote",
      included: [
        {
          title: "Your dedicated account manager",
          text: "One person who knows your business — and picks up the phone.",
          badge: "INCLUDED"
        },
        {
          title: "Paperless bookkeeping & plain-language monthly reports",
          text: "Know where you stand every month, not once a year.",
          badge: "INCLUDED"
        },
        {
          title: "VAT returns",
          text: "Filed correctly and on time, monthly or quarterly.",
          badge: "INCLUDED"
        },
        {
          title: "Payroll",
          text: "Your people paid correctly and legally — including foreign workers.",
          badge: "INCLUDED"
        },
        {
          title: "BONUS: Done-for-you switch-over",
          text: "We handle the handover from your old accountant — even mid-year.",
          badge: "FREE",
          isBonus: true
        }
      ],
      additionalHeader: "ADDITIONAL SERVICES — BILLED SEPARATELY, AT A PRICE AGREED IN ADVANCE",
      additional: [
        { title: "EU VAT refunds", text: "Refund claims for fuel, tolls and services paid abroad." },
        { title: "HR paperwork", text: "Employment contracts, medical check-up referrals, insurance registrations, A1 forms." },
        { title: "Work permit applications", text: "Single residence and work permits, renewals." },
        { title: "Reports for banks", text: "Documentation and reports for loan and financing applications." },
        { title: "Other services", text: "Tax advice, company formation, business rescue and more — by agreement." }
      ],
      transparent: {
        label: "TRANSPARENT PRICING",
        title: "Core services for one fixed monthly fee.",
        text: "Additional services always agreed in advance — no surprises on your invoice. Written quote within 48 hours."
      }
    }
  };
  
  const t = content[language] || content.sl;
  
  const scrollToOffer = () => {
    const element = document.getElementById('offer');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section className="py-24 lg:py-32 px-6 lg:px-[120px] bg-[#F6F2EA]" data-testid="package-section">
      <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-20">
        {/* Left side - Intro */}
        <div className="w-full lg:w-[460px] flex-shrink-0 flex flex-col gap-6 animate-on-scroll from-left">
          <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28]">{t.label}</span>
          <h2 className="font-serif text-3xl lg:text-[60px] leading-[1.02] tracking-[-1px] text-[#15181C]">
            {t.title} <span className="italic">{t.titleHighlight}</span>
          </h2>
          <p className="text-[17px] leading-[1.65] text-[#4E5359]">{t.description}</p>
          <button 
            onClick={scrollToOffer}
            className="self-start mt-2 flex items-center gap-3 bg-[#0E1114] text-[#F6F2EA] font-bold text-base px-8 py-5 hover:bg-[#1a1f25] transition-colors"
            data-testid="package-cta"
          >
            {t.cta}
            <ArrowRight className="w-[18px] h-[18px]" />
          </button>
        </div>
        
        {/* Right side - Package list */}
        <div className="flex-grow bg-[#FFFDF8] border border-[#E2DACB] flex flex-col animate-on-scroll from-right delay-2">
          {/* Included items */}
          {t.included.map((item) => (
            <div 
              key={item.title}
              className="px-6 lg:px-9 py-6 flex flex-col sm:flex-row justify-between gap-4 border-b border-[#E2DACB]"
            >
              <div className="flex gap-4">
                <Check className="w-[22px] h-[22px] text-[#7A5B28] flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex flex-col gap-1">
                  <span className="text-[17px] font-bold text-[#15181C]">{item.title}</span>
                  <span className="text-sm text-[#4E5359]">{item.text}</span>
                </div>
              </div>
              <span className={`text-xs font-bold tracking-[1.5px] whitespace-nowrap ${item.isBonus ? 'text-[#7A5B28] text-[15px]' : 'text-[#7A5B28]'}`}>
                {item.badge}
              </span>
            </div>
          ))}
          
          {/* Additional services header */}
          <div className="px-6 lg:px-9 py-5 bg-[#F1ECE1] border-b border-[#E2DACB]">
            <span className="text-xs font-bold tracking-[1.5px] text-[#4E5359]">{t.additionalHeader}</span>
          </div>
          
          {/* Additional items */}
          {t.additional.map((item) => (
            <div 
              key={item.title}
              className="px-6 lg:px-9 py-5 flex flex-col sm:flex-row justify-between gap-4 border-b border-[#E2DACB]"
            >
              <div className="flex gap-4">
                <Plus className="w-[22px] h-[22px] text-[#4E5359] flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex flex-col gap-1">
                  <span className="text-[17px] font-bold text-[#15181C]">{item.title}</span>
                  <span className="text-sm text-[#4E5359]">{item.text}</span>
                </div>
              </div>
              <span className="text-xs font-bold tracking-[1.5px] text-[#4E5359] whitespace-nowrap">
                {language === 'en' ? 'ADDITIONAL' : 'DODATNO'}
              </span>
            </div>
          ))}
          
          {/* Transparent pricing band */}
          <div className="px-6 lg:px-9 py-8 bg-[#0E1114] text-[#F4EFE6]">
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] tracking-[2px] font-bold text-[#C9A462]">{t.transparent.label}</span>
              <span className="font-serif text-2xl lg:text-[34px]">{t.transparent.title}</span>
              <span className="text-sm text-[#B9B3A7]">{t.transparent.text}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PackageSection;
